import React, { useState, useRef, useEffect } from 'react';
import type { MenuItem, Category, QuickAddCombo, Recipe, InventoryItem, BuffetPackage } from '../types';
import PlusIcon from '../components/icons/PlusIcon';
import EditIcon from '../components/icons/EditIcon';
import TrashIcon from '../components/icons/TrashIcon';
import MenuItemModal from '../components/menu/MenuItemModal';
import MenuItemImage from '../components/menu/MenuItemImage';
import CategoryModal from '../components/menu/CategoryModal';
import QuickAddComboModal from '../components/menu/QuickAddComboModal';
import BuffetPackagesPanel from '../components/menu/BuffetPackagesPanel';
import RecipeManagementModal from '../components/inventory/RecipeManagementModal';
import DownloadIcon from '../components/icons/DownloadIcon';
import UploadIcon from '../components/icons/UploadIcon';
import StarSolidIcon from '../components/icons/StarSolidIcon';
import StarIcon from '../components/icons/StarIcon';
import ZapIcon from '../components/icons/ZapIcon';
import { getDisabledChannelLabels } from '../lib/menuChannelAvailability';
import { pushMenuToCustomerApp } from '../lib/pushMenuToCustomerApp';

interface MenuManagementPageProps {
  menuItems: MenuItem[];
  categories: Category[];
  quickAddCombos: QuickAddCombo[];
  recipes: Recipe[];
  inventoryItems: InventoryItem[];
  recipeInventoryMode: 'inventory_connected' | 'cost_only';
  buffetPackages?: BuffetPackage[];
  onSaveBuffetPackages?: (packages: BuffetPackage[]) => Promise<void> | void;
  onSave: (item: MenuItem) => void;
  onDelete: (itemId: number) => void;
  onSaveCategory: (category: Category) => void;
  onDeleteCategory: (categoryId: number | string) => void;
  onBulkAddMenuItems: (items: any[]) => Promise<{ successCount: number, failedItems: string[] }>;
  onSaveCombo: (combo: Omit<QuickAddCombo, 'id'> | QuickAddCombo) => Promise<void>;
  onDeleteCombo: (comboId: number) => Promise<void>;
  onSaveRecipe: (recipe: Recipe) => void;
}

const MenuManagementPage: React.FC<MenuManagementPageProps> = ({
  menuItems,
  categories,
  quickAddCombos,
  recipes,
  inventoryItems,
  recipeInventoryMode,
  buffetPackages = [],
  onSaveBuffetPackages,
  onSave,
  onDelete,
  onSaveCategory,
  onDeleteCategory,
  onBulkAddMenuItems,
  onSaveCombo,
  onDeleteCombo,
  onSaveRecipe,
}) => {
  const [activeTab, setActiveTab] = useState<'items' | 'categories' | 'combos' | 'buffet'>('items');
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | string>(categories[0]?.id ?? 1);
  
  const [isItemModalOpen, setIsItemModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<MenuItem | null>(null);

  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);

  const [isComboModalOpen, setIsComboModalOpen] = useState(false);
  const [editingCombo, setEditingCombo] = useState<QuickAddCombo | null>(null);

  const [isRecipeModalOpen, setIsRecipeModalOpen] = useState(false);
  const [editingRecipeItem, setEditingRecipeItem] = useState<MenuItem | null>(null);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const appMenuPushTimer = useRef<number | null>(null);
  const appMenuPushBusy = useRef(false);
  const appMenuPushNow = useRef<() => Promise<void>>(async () => {});
  const appMenuPushGeneration = useRef(0);
  const appMenuPushAgain = useRef(false);
  const [appSyncBusy, setAppSyncBusy] = useState(false);
  const [appSyncStatus, setAppSyncStatus] = useState<string | null>(null);
  const [pushPromptOpen, setPushPromptOpen] = useState(false);

  const scheduleAppMenuPush = () => {
    setPushPromptOpen(true);
  };

  const pushAppMenuNow = async () => {
    if (appMenuPushTimer.current) {
      window.clearTimeout(appMenuPushTimer.current);
      appMenuPushTimer.current = null;
    }
    if (appMenuPushBusy.current) {
      appMenuPushAgain.current = true;
      return;
    }
    appMenuPushBusy.current = true;
    const generation = appMenuPushGeneration.current;
    setAppSyncBusy(true);
    setAppSyncStatus('Updating customer app…');
    try {
      const result = await pushMenuToCustomerApp();
      if (generation === appMenuPushGeneration.current && !appMenuPushAgain.current) {
        setAppSyncStatus(`Updated — ${result.synced} items`);
      }
    } catch (err) {
      if (generation === appMenuPushGeneration.current && !appMenuPushAgain.current) {
        setAppSyncStatus(err instanceof Error ? err.message : 'Could not update the customer app.');
      }
    } finally {
      appMenuPushBusy.current = false;
      setAppSyncBusy(false);
      if (appMenuPushAgain.current) {
        appMenuPushAgain.current = false;
        void appMenuPushNow.current();
      }
    }
  };
  appMenuPushNow.current = pushAppMenuNow;

  useEffect(() => () => {
    if (appMenuPushTimer.current) window.clearTimeout(appMenuPushTimer.current);
  }, []);

  // Keep selected category in sync when categories load or change (e.g. after adding a new category)
  useEffect(() => {
    if (categories.length === 0) return;
    const currentNorm = String(selectedCategoryId);
    const exists = categories.some((c) => String(c.id) === currentNorm);
    if (!exists) {
      setSelectedCategoryId(categories[0].id);
    }
  }, [categories]);

  const handleOpenItemModal = (item: MenuItem | null = null) => {
    setEditingItem(item);
    setIsItemModalOpen(true);
  };
  const handleCloseItemModal = () => setIsItemModalOpen(false);
  const handleSaveItem = async (item: MenuItem) => {
    await onSave(item);
    handleCloseItemModal();
    scheduleAppMenuPush();
  };
  const handleDeleteItem = (itemId: number) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
        onDelete(itemId);
        scheduleAppMenuPush();
    }
  };

  const handleOpenRecipeModal = (item: MenuItem) => {
    setEditingRecipeItem(item);
    setIsRecipeModalOpen(true);
  };

  const handleCloseRecipeModal = () => {
    setIsRecipeModalOpen(false);
    setEditingRecipeItem(null);
  };

  const handleSaveRecipe = (recipe: Recipe) => {
    onSaveRecipe(recipe);
    handleCloseRecipeModal();
  };

  const handleOpenCategoryModal = (category: Category | null = null) => {
    setEditingCategory(category);
    setIsCategoryModalOpen(true);
  };
  const handleCloseCategoryModal = () => setIsCategoryModalOpen(false);
  const handleSaveCategory = async (category: Category) => {
    await onSaveCategory(category);
    handleCloseCategoryModal();
    scheduleAppMenuPush();
  };
  const handleDeleteCategory = (categoryId: number | string) => {
    if (window.confirm('Are you sure you want to delete this category?')) {
        onDeleteCategory(categoryId);
        scheduleAppMenuPush();
    }
  };

  const handleOpenComboModal = (combo: QuickAddCombo | null = null) => {
    setEditingCombo(combo);
    setIsComboModalOpen(true);
  };
  const handleCloseComboModal = () => setIsComboModalOpen(false);
  const handleSaveCombo = async (combo: Omit<QuickAddCombo, 'id'> | QuickAddCombo) => {
    await onSaveCombo(combo);
    handleCloseComboModal();
  };
  const handleDeleteCombo = async (comboId: number) => {
    if (window.confirm('Are you sure you want to delete this combo?')) {
        await onDeleteCombo(comboId);
    }
  };

  const handleDownloadTemplate = () => {
    const header = "Category,Name,Price,Is Signature Dish (TRUE/FALSE)\n";
    const example = "Mains,Fish and Chips,25.00,TRUE\n";
    const csvContent = header + example;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", "menu_template.csv");
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
        const text = (e.target?.result as string)?.replace(/^\uFEFF/, ''); // Remove BOM
        if (!text) {
          alert("Error: Could not read the file.");
          return;
        }

        try {
            const rows = text.split(/\r?\n/).filter(row => row.trim() !== '');
            if (rows.length < 2) {
                alert("Error: CSV file is empty or contains only a header. Please add item data below the header row.");
                return;
            }

            const headerRow = rows[0].split(',').map(h => h.trim().toLowerCase().replace(/"/g, ''));
            const requiredHeaders = ['category', 'name', 'price'];
            if (!requiredHeaders.every(h => headerRow.includes(h))) {
                alert(`Invalid CSV format. Header row must contain at least these columns: ${requiredHeaders.join(', ')}`);
                return;
            }
            
            const itemsToAdd: any[] = [];
            const parsingErrors: string[] = [];

            rows.slice(1).forEach((row, index) => {
                const values = row.split(',').map(v => v.trim().replace(/"/g, ''));
                if (values.length !== headerRow.length) {
                    parsingErrors.push(`Row ${index + 2}: Incorrect number of columns. Expected ${headerRow.length}, but found ${values.length}.`);
                    return;
                }
                
                const item: any = {};
                headerRow.forEach((h, i) => {
                    let key = h.replace(/ /g, '').replace(/\(true\/false\)/i, '').replace('issignaturedish', 'isSignatureDish');
                    if (key === 'category') key = 'categoryName';
                    item[key] = values[i];
                });

                item.price = parseFloat(String(item.price).replace(/[^0-9.-]+/g, ""));

                if (!item.name || !item.categoryName || isNaN(item.price)) {
                    parsingErrors.push(`Row ${index + 2}: Missing or invalid data for Name, Category, or Price. Please check the values.`);
                    return;
                }
                item.isSignatureDish = item.isSignatureDish?.toString().toUpperCase() === 'TRUE';
                itemsToAdd.push(item);
            });
            
            let feedbackMessages: string[] = [];

            if (itemsToAdd.length > 0) {
              const result = await onBulkAddMenuItems(itemsToAdd);
              if(result && result.successCount > 0) {
                feedbackMessages.push(`✅ Success: ${result.successCount} items were successfully added.`);
                scheduleAppMenuPush();
              }
              if(result && result.failedItems && result.failedItems.length > 0) {
                feedbackMessages.push(`⚠️ Category Not Found: Could not add ${result.failedItems.length} items because their category was not found in the system:\n- ${result.failedItems.join('\n- ')}`);
              }
            }
            
            if (parsingErrors.length > 0) {
              feedbackMessages.push(`❌ Parsing Errors: The following ${parsingErrors.length} rows in the file could not be read:\n- ${parsingErrors.join('\n- ')}`);
            }

            if (feedbackMessages.length > 0) {
              alert(feedbackMessages.join('\n\n'));
            } else {
              alert('No valid items found to add from the CSV file. Please check the file content and format.');
            }

        } catch (error) {
            console.error("Error parsing CSV:", error);
            alert("A critical error occurred while parsing the CSV file. Please check its format.");
        } finally {
            if (fileInputRef.current) {
                fileInputRef.current.value = ''; // Reset file input
            }
        }
    };
    reader.readAsText(file);
  };

  // Compare as strings so numeric and Firestore string category ids both match correctly (each category separate)
  const selectedIdNorm = String(selectedCategoryId);
  const displayedItems = menuItems.filter((item) => String(item.categoryId) === selectedIdNorm);

  return (
    <div className="flex flex-col h-full">
      <header className="bg-white shadow-md z-10 flex-shrink-0">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between gap-3 min-h-16 py-2">
            <div className="min-w-0">
              <h1 className="text-2xl font-bold text-gray-800" data-testid="menu-page-heading">Menu Management</h1>
              {appSyncStatus && (
                <p className="text-xs text-gray-500 truncate" role="status">{appSyncStatus}</p>
              )}
            </div>
            <div className="flex items-center space-x-2 flex-wrap justify-end">
                <button
                  type="button"
                  onClick={() => void pushAppMenuNow()}
                  disabled={appSyncBusy}
                  className="bg-emerald-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-emerald-700 disabled:opacity-60 transition-colors duration-300"
                >
                  {appSyncBusy ? 'Updating app…' : 'Update customer app'}
                </button>
                <button onClick={handleDownloadTemplate} className="bg-gray-200 text-gray-800 font-bold py-2 px-4 rounded-lg hover:bg-gray-300 transition-colors duration-300 flex items-center">
                    <DownloadIcon className="w-5 h-5 mr-2" /> Download Template
                </button>
                 <input type="file" ref={fileInputRef} onChange={handleFileUpload} accept=".csv" className="hidden" />
                <button onClick={handleUploadClick} className="bg-blue-500 text-white font-bold py-2 px-4 rounded-lg hover:bg-blue-600 transition-colors duration-300 flex items-center">
                    <UploadIcon className="w-5 h-5 mr-2" /> Upload CSV
                </button>
                <button
                  onClick={() => {
                    if (activeTab === 'items') handleOpenItemModal();
                    else if (activeTab === 'categories') handleOpenCategoryModal();
                    else if (activeTab === 'combos') handleOpenComboModal();
                  }}
                  className={`bg-pink-500 text-white font-bold py-2 px-4 rounded-lg hover:bg-pink-600 transition-colors duration-300 flex items-center ${activeTab === 'buffet' ? 'invisible' : ''}`}
                >
                  <PlusIcon className="w-5 h-5 mr-2" />
                  {activeTab === 'items' ? 'Add New Item' : activeTab === 'categories' ? 'Add New Category' : 'Add Combo'}
                </button>
            </div>
          </div>
        </div>
      </header>
      <main className="flex-1 p-4 sm:p-6 overflow-y-auto">
        <div className="mb-6 border-b border-gray-200">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            <button onClick={() => setActiveTab('items')} className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'items' ? 'border-pink-500 text-pink-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>
              Menu Items
            </button>
            <button onClick={() => setActiveTab('categories')} className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'categories' ? 'border-pink-500 text-pink-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>
              Categories
            </button>
            <button onClick={() => setActiveTab('combos')} className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'combos' ? 'border-pink-500 text-pink-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>
              Quick Add Combos
            </button>
            <button onClick={() => setActiveTab('buffet')} className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'buffet' ? 'border-pink-500 text-pink-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>
              Buffet packages
            </button>
          </nav>
        </div>
        
        {activeTab === 'items' && (
          <div>
            <div className="mb-6 border-b border-gray-200">
              <nav className="-mb-px flex flex-wrap gap-x-6 gap-y-2" aria-label="Tabs">
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategoryId(category.id)}
                    className={`${
                      String(category.id) === String(selectedCategoryId)
                        ? 'border-pink-500 text-pink-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                  >
                    {category.name}
                  </button>
                ))}
              </nav>
            </div>
            
            <div className="bg-white shadow rounded-lg overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Image</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Availability</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Signature</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Recipe</th>
                            <th scope="col" className="relative px-6 py-3">
                                <span className="sr-only">Actions</span>
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {displayedItems.map((item) => (
                            <tr key={item.id}>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <MenuItemImage className="h-10 w-10 rounded-md" src={item.imageUrlThumb || item.imageUrl || `https://picsum.photos/seed/${item.id}/200`} alt={item.name} width={40} height={40} />
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm font-medium text-gray-900">{item.name}</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-900">${item.price.toFixed(2)}</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="space-y-1 text-sm">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        onSave({ ...item, isAvailable: item.isAvailable === false ? true : false });
                                        scheduleAppMenuPush();
                                      }}
                                      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                        item.isAvailable === false
                                          ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                          : 'bg-green-100 text-green-800 hover:bg-green-200'
                                      }`}
                                    >
                                      <span
                                        className={`inline-block w-2 h-2 rounded-full mr-1 ${
                                          item.isAvailable === false ? 'bg-gray-500' : 'bg-green-500'
                                        }`}
                                      />
                                      {item.isAvailable === false ? 'Hidden from ordering' : 'Available'}
                                    </button>
                                    {item.isAvailable !== false && getDisabledChannelLabels(item).length > 0 && (
                                      <div className="flex flex-wrap gap-1">
                                        {getDisabledChannelLabels(item).map((label) => (
                                          <span
                                            key={label}
                                            className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-50 text-amber-800"
                                          >
                                            No {label}
                                          </span>
                                        ))}
                                      </div>
                                    )}
                                    {item.hasDailyAvailability && (
                                      <div className="text-xs">
                                        {item.dailyAvailability !== undefined && item.dailyAvailability > 0 ? (
                                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700">
                                            {item.dailyAvailability} available today
                                          </span>
                                        ) : (
                                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700">
                                            Finished for today
                                          </span>
                                        )}
                                        {item.dailyAvailabilityLimit && (
                                          <div className="text-[10px] text-gray-500 mt-0.5">
                                            Daily limit: {item.dailyAvailabilityLimit}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    {item.isSignatureDish && (
                                        <StarSolidIcon className="w-5 h-5 text-yellow-500" title="Signature Dish" />
                                    )}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    {recipes.find(r => r.menuItemId === item.id) ? (
                                        <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">Has Recipe</span>
                                    ) : (
                                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">No Recipe</span>
                                    )}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    <button onClick={() => handleOpenRecipeModal(item)} className="text-blue-600 hover:text-blue-900 mr-4" title="Manage Recipe">
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                        </svg>
                                    </button>
                                    <button onClick={() => handleOpenItemModal(item)} className="text-pink-600 hover:text-pink-900 mr-4"><EditIcon className="w-5 h-5" /></button>
                                    <button onClick={() => handleDeleteItem(item.id)} className="text-red-600 hover:text-red-900"><TrashIcon className="w-5 h-5" /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
          </div>
        )}

        {activeTab === 'categories' && (
            <div className="bg-white shadow rounded-lg overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category Name</th>
                             <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Items in Category</th>
                            <th scope="col" className="relative px-6 py-3">
                                <span className="sr-only">Actions</span>
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {categories.map((cat) => (
                            <tr key={cat.id}>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm font-medium text-gray-900">{cat.name}</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-500">{menuItems.filter(i => String(i.categoryId) === String(cat.id)).length}</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    <button onClick={() => handleOpenCategoryModal(cat)} className="text-pink-600 hover:text-pink-900 mr-4"><EditIcon className="w-5 h-5" /></button>
                                    <button onClick={() => handleDeleteCategory(cat.id)} className="text-red-600 hover:text-red-900"><TrashIcon className="w-5 h-5" /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        )}

        {activeTab === 'combos' && (
          <div>
            <div className="mb-4 flex justify-end">
              <button
                onClick={() => handleOpenComboModal(null)}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-pink-600 hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500"
              >
                <PlusIcon className="w-5 h-5 mr-2" />
                Create Combo
              </button>
            </div>
            <div className="bg-white shadow rounded-lg overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Icon</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Combo Name</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Items</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Display Order</th>
                    <th scope="col" className="relative px-6 py-3">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {quickAddCombos.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-8 text-center text-sm text-gray-500">
                        No quick add combos created yet. Click "Create Combo" to add one.
                      </td>
                    </tr>
                  ) : (
                    quickAddCombos.map((combo) => {
                      const Icon = combo.iconType === 'star' ? StarIcon : ZapIcon;
                      // Calculate individual items total
                      const comboItems = Array.isArray(combo.items) ? combo.items : [];
                      const individualTotal = comboItems.reduce((sum, item) => {
                        const menuItem = menuItems.find(m => m.id === item.menuItemId);
                        return sum + (menuItem ? menuItem.price * item.quantity : 0);
                      }, 0);
                      return (
                        <tr key={combo.id}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Icon className="w-5 h-5 text-pink-600" />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{combo.name}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm">
                              {combo.price && combo.price > 0 ? (
                                <div>
                                  <span className="font-bold text-green-600">${combo.price.toFixed(2)}</span>
                                  {combo.price < individualTotal && (
                                    <span className="text-xs text-gray-500 block line-through">${individualTotal.toFixed(2)}</span>
                                  )}
                                </div>
                              ) : (
                                <span className="text-gray-500">${individualTotal.toFixed(2)}</span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-500">
                              {comboItems.length === 0 ? (
                                <span className="text-gray-400 italic">No items</span>
                              ) : (
                                <div className="space-y-1">
                                  {comboItems.map((item, idx) => {
                                    const menuItem = menuItems.find(m => m.id === item.menuItemId);
                                    return (
                                      <div key={idx} className="text-xs">
                                        {item.quantity}x {menuItem?.name || `Item #${item.menuItemId}`}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-500">{combo.displayOrder || 0}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <button onClick={() => handleOpenComboModal(combo)} className="text-pink-600 hover:text-pink-900 mr-4">
                              <EditIcon className="w-5 h-5" />
                            </button>
                            <button onClick={() => handleDeleteCombo(combo.id)} className="text-red-600 hover:text-red-900">
                              <TrashIcon className="w-5 h-5" />
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'buffet' && onSaveBuffetPackages && (
          <div className="bg-white shadow rounded-lg p-6">
            <BuffetPackagesPanel packages={buffetPackages} onSavePackages={onSaveBuffetPackages} />
          </div>
        )}
        {activeTab === 'buffet' && !onSaveBuffetPackages && (
          <p className="text-sm text-gray-500">Buffet package editing is unavailable.</p>
        )}
      </main>
      {isItemModalOpen && (
        <MenuItemModal
            item={editingItem}
            categories={categories}
            defaultCategoryId={selectedCategoryId}
            onClose={handleCloseItemModal}
            onSave={handleSaveItem}
        />
      )}
      {isCategoryModalOpen && (
        <CategoryModal
            category={editingCategory}
            onClose={handleCloseCategoryModal}
            onSave={handleSaveCategory}
        />
      )}
      {isComboModalOpen && (
        <QuickAddComboModal
          combo={editingCombo}
          menuItems={menuItems}
          categories={categories}
          onClose={handleCloseComboModal}
          onSave={handleSaveCombo}
        />
      )}
      {isRecipeModalOpen && editingRecipeItem && (
        <RecipeManagementModal
          isOpen={isRecipeModalOpen}
          menuItem={editingRecipeItem}
          recipeInventoryMode={recipeInventoryMode}
          inventoryItems={inventoryItems}
          existingRecipe={recipes.find(r => r.menuItemId === editingRecipeItem.id)}
          otherMenuItemsWithRecipes={recipes
            .filter(r => r.menuItemId !== editingRecipeItem.id && r.ingredients.length > 0)
            .map(r => ({ recipe: r, menuItem: menuItems.find(m => m.id === r.menuItemId)! }))
            .filter(({ menuItem }) => menuItem != null)}
          onClose={handleCloseRecipeModal}
          onSave={handleSaveRecipe}
        />
      )}
      {pushPromptOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6" role="dialog" aria-modal="true" aria-labelledby="push-menu-title">
            <h2 id="push-menu-title" className="text-lg font-bold text-gray-900">Update the customer app?</h2>
            <p className="mt-2 text-sm text-gray-600">
              This change is saved on the till. Phones still show the previous menu until you update the app, so customers can order the old dishes until then.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setPushPromptOpen(false)}
                className="bg-gray-200 text-gray-800 font-bold py-2 px-4 rounded-lg hover:bg-gray-300"
              >
                Not now
              </button>
              <button
                type="button"
                onClick={() => {
                  setPushPromptOpen(false);
                  void pushAppMenuNow();
                }}
                disabled={appSyncBusy}
                className="bg-emerald-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-emerald-700 disabled:opacity-60"
              >
                Update customer app
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MenuManagementPage;
